import { Stack } from '@optimus-web/core';
import { useInvalidateRepositoryQuery } from '@mews-commander/data-access';
import { useCityLedgerMultipleInvoicesContext } from '../../context/CityLedgerMultipleInvoicesContext';
import { ProcessPaymentDialogContent } from '../../../../../payment/components/forms/ProcessPayment/ProcessPaymentDialogContent';
import { PaymentFormRef } from '../../../../../payment/components/forms/ProcessPayment/types';
import { getFilteredProcessPaymentDialogTypes } from '../../../../../payment/components/forms/ProcessPayment/utils';
import { useGetChargeGuestOptionsSuspense } from '../../../../../payment/components/forms/ProcessPayment/hooks/useGetChargeGuestOptionsSuspense';
import { useAllowedPaymentMethodsForInvoices } from '../../../../../customerDetail/components/tables/BillsTable/constants';
import { PayerSummary } from './PayerSummary';

interface TwoStepProcessPaymentProps {
    payerAccountId: string;
    setIsLoading: (isLoading: boolean) => void;
    onClose: () => void;
    formRef: React.RefObject<PaymentFormRef>;
    dialogType: 'external' | 'default';
}

const usePaymentTypeSuspense = (dialogType: 'external' | 'default') => {
    const allowedPaymentTypes = useAllowedPaymentMethodsForInvoices();
    const chargeGuestOptions = useGetChargeGuestOptionsSuspense({ allowedPaymentTypes });
    const { chargeGuestPaymentTypes, externalPaymentTypes } = getFilteredProcessPaymentDialogTypes(allowedPaymentTypes);

    if (dialogType === 'external') {
        return externalPaymentTypes;
    }

    return chargeGuestOptions.length > 0 ? chargeGuestPaymentTypes : externalPaymentTypes;
};

export const TwoStepProcessPayment = ({
    payerAccountId,
    setIsLoading,
    onClose,
    dialogType,
    formRef,
}: TwoStepProcessPaymentProps) => {
    const { setSelectedInvoices, invoiceIds, ownerIds: multipleOwnersIds } = useCityLedgerMultipleInvoicesContext();
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    const paymentType = usePaymentTypeSuspense(dialogType);

    return (
        <Stack vertical spacing="200">
            <ProcessPaymentDialogContent
                defaultSelectedFormValues={{ invoiceIds }}
                allowedPaymentTypes={paymentType}
                currentFormRef={formRef}
                accountId={payerAccountId}
                canLinkInvoices
                isLinkingInvoiceDropdownVisible={false}
                ownerIds={multipleOwnersIds}
                onFormSubmitting={() => {
                    setIsLoading(true);
                }}
                onFormSubmitted={() => {
                    setIsLoading(false);
                }}
                onFormSuccess={() => {
                    void invalidateRepositoryQuery('invoices', 'getAll', {
                        ownerIds: multipleOwnersIds,
                        invoiceIds,
                    });

                    void invalidateRepositoryQuery('cityLedger', 'balances');

                    setSelectedInvoices([]);
                    onClose();
                }}
            />
            <PayerSummary payerAccountId={payerAccountId} />
        </Stack>
    );
};
